import { Fragment, useEffect, useMemo, useState } from "react";
import api, { formatApiError, formatMoney } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Lock, Calendar, FileText, CheckCircle2, RotateCcw, Eye, Printer, AlertTriangle, History } from "lucide-react";
import { toast } from "sonner";
import { ReportePrevio } from "@/components/cierre/ReportePrevio";
import { CuadreDashboard } from "@/components/cierre/CuadreDashboard";

// -------- helpers fechas --------
function isoDay(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }
function firstDayOfMonth() { const d = new Date(); return isoDay(new Date(d.getFullYear(), d.getMonth(), 1)); }
function lastDayOfMonth() { const d = new Date(); return isoDay(new Date(d.getFullYear(), d.getMonth() + 1, 0)); }

const ESTADO_LABEL = {
  facturado_full: { txt: "100% fact.", cls: "bg-emerald-600 text-white" },
  facturado_parcial: { txt: "Parcial", cls: "bg-amber-500 text-white" },
  sin_facturar: { txt: "S/factura", cls: "bg-zinc-300 text-zinc-800" },
  cancelado: { txt: "Cancelado", cls: "bg-red-600 text-white" },
};

export default function CierreMes() {
  const [tab, setTab] = useState("nuevo"); // nuevo | historial
  const [desde, setDesde] = useState(firstDayOfMonth());
  const [hasta, setHasta] = useState(isoDay(new Date()));
  const [snapshot, setSnapshot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [reabrir, setReabrir] = useState({ open: false, id: null, motivo: "" });
  const [expandedCierre, setExpandedCierre] = useState({}); // {cid: snapshot}
  const [ult, setUlt] = useState(null);
  const [modoUlt, setModoUlt] = useState(true);
  useEffect(() => { api.get("/cierres-mes-ultimo").then(({ data }) => setUlt(data)).catch(() => {}); }, []);
  const autoOk = modoUlt && ult;
  const mesDefault = (() => { const [y, m, d] = hasta.split("-").map(Number); if (d > 10) return hasta.slice(0, 7); const p = new Date(y, m - 2, 1); return `${p.getFullYear()}-${String(p.getMonth() + 1).padStart(2, "0")}`; })();
  const [mesKey, setMesKey] = useState("");
  const mes = mesKey || mesDefault;
  const rango = autoOk
    ? { desde: ult.desde, hasta, mes_key: mes, ...(ult.ejecutado_at ? { desde_ts: ult.ejecutado_at } : {}) }
    : { desde, hasta, mes_key: mes };

  const toggleCierreExpand = async (c) => {
    if (expandedCierre[c.id]) {
      setExpandedCierre((s) => { const n = { ...s }; delete n[c.id]; return n; });
      return;
    }
    try {
      const { data } = await api.get(`/cierres-mes/${c.id}`);
      setExpandedCierre((s) => ({ ...s, [c.id]: data }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cargarHistorial = async () => {
    try { const { data } = await api.get("/cierres-mes"); setHistorial(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { cargarHistorial(); }, []);

  const previsualizar = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/cierres-mes/preview", rango);
      setSnapshot(data);
      toast.success("Pre-cierre calculado");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const descargarPreviewPdf = async () => {
    try {
      const { data } = await api.post("/cierres-mes/preview.pdf", rango, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      link.download = `precierre_${desde}_${hasta}.pdf`;
      document.body.appendChild(link); link.click(); link.remove(); setTimeout(() => URL.revokeObjectURL(link.href), 60000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmar = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/cierres-mes", rango);
      toast.success(`Cierre #${data.numero_cierre} confirmado`);
      setSnapshot(null); setConfirmOpen(false); setTab("historial");
      await cargarHistorial();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const descargarCierrePdf = async (cid) => {
    try {
      const { data } = await api.get(`/cierres-mes/${cid}/pdf`, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      link.download = `cierre_${cid.slice(0, 8)}.pdf`;
      document.body.appendChild(link); link.click(); link.remove(); setTimeout(() => URL.revokeObjectURL(link.href), 60000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const descargarBancoPreview = async (banco, fmt) => {
    try {
      const path = fmt === "xlsx" ? "/cierres-mes/preview/banco.xlsx" : "/cierres-mes/preview/banco.pdf";
      const { data } = await api.post(`${path}?banco=${encodeURIComponent(banco)}`, rango, { responseType: "blob" });
      const mime = fmt === "xlsx"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/pdf";
      const blob = new Blob([data], { type: mime });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `banco_${banco.replace(/[^a-zA-Z0-9_-]+/g, "_")}_${desde}_${hasta}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 60000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const descargarBancoHistorial = async (cid, banco, fmt) => {
    try {
      const path = fmt === "xlsx" ? `/cierres-mes/${cid}/banco.xlsx` : `/cierres-mes/${cid}/banco.pdf`;
      const { data } = await api.get(`${path}?banco=${encodeURIComponent(banco)}`, { responseType: "blob" });
      const mime = fmt === "xlsx"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/pdf";
      const blob = new Blob([data], { type: mime });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `banco_${banco.replace(/[^a-zA-Z0-9_-]+/g, "_")}_cierre_${cid.slice(0,8)}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 60000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Ronda 21+23 — TODOS (consolidado) y filtros por tipo de envío
  const descargarTodosPreview = async (fmt, tipo_envio = null) => {
    try {
      const base = fmt === "xlsx" ? "/cierres-mes/preview/todos.xlsx" : "/cierres-mes/preview/todos.pdf";
      const path = tipo_envio ? `${base}?tipo_envio=${tipo_envio}` : base;
      const { data } = await api.post(path, rango, { responseType: "blob" });
      const mime = fmt === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "application/pdf";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([data], { type: mime }));
      const suf = tipo_envio ? `_${tipo_envio}` : "";
      a.download = `todos${suf}_${desde}_${hasta}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 60000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const descargarTodosHistorial = async (cid, fmt, tipo_envio = null) => {
    try {
      const base = fmt === "xlsx" ? `/cierres-mes/${cid}/todos.xlsx` : `/cierres-mes/${cid}/todos.pdf`;
      const path = tipo_envio ? `${base}?tipo_envio=${tipo_envio}` : base;
      const { data } = await api.get(path, { responseType: "blob" });
      const mime = fmt === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "application/pdf";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([data], { type: mime }));
      const suf = tipo_envio ? `_${tipo_envio}` : "";
      a.download = `todos${suf}_cierre_${cid.slice(0,8)}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 60000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const descargarConciliacionPreview = async (fmt) => {
    try {
      const path = fmt === "xlsx" ? "/cierres-mes/preview/conciliacion.xlsx" : "/cierres-mes/preview/conciliacion.pdf";
      const { data } = await api.post(path, rango, { responseType: "blob" });
      const mime = fmt === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "application/pdf";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([data], { type: mime }));
      a.download = `conciliacion_${desde}_${hasta}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 60000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Ronda 23 — Pendientes de facturar (reporte imprimible)
  const descargarPendientesPreview = async (fmt) => {
    try {
      const path = fmt === "xlsx" ? "/cierres-mes/preview/pendientes.xlsx" : "/cierres-mes/preview/pendientes.pdf";
      const { data } = await api.post(path, rango, { responseType: "blob" });
      const mime = fmt === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "application/pdf";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([data], { type: mime }));
      a.download = `pendientes_${desde}_${hasta}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 60000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const descargarPendientesHistorial = async (cid, fmt) => {
    try {
      const path = fmt === "xlsx" ? `/cierres-mes/${cid}/pendientes.xlsx` : `/cierres-mes/${cid}/pendientes.pdf`;
      const { data } = await api.get(path, { responseType: "blob" });
      const mime = fmt === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "application/pdf";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([data], { type: mime }));
      a.download = `pendientes_cierre_${cid.slice(0,8)}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 60000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doReabrir = async () => {
    if (!reabrir.motivo.trim()) return toast.error("El motivo es obligatorio");
    try {
      await api.post(`/cierres-mes/${reabrir.id}/reabrir`, { motivo: reabrir.motivo });
      toast.success("Cierre reabierto — los pedidos y gastos regresaron a Activo");
      setReabrir({ open: false, id: null, motivo: "" });
      await cargarHistorial();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="cierre-mes-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Administración financiera</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
            <Lock className="w-8 h-8 text-[#002FA7]" /> Cierre de Mes
          </h1>
          <p className="text-zinc-500 mt-2 text-sm max-w-2xl">
            Congela los movimientos del período en un snapshot inmutable. Los pedidos 100% facturados
            y cancelados pasan a Historial financiero; los no entregados siguen en Hoja de Pedidos. Mis Ventas y App Domicilios no se reinician.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setTab("nuevo")} className={`px-4 py-2 rounded font-black uppercase tracking-widest text-xs ${tab === "nuevo" ? "bg-black text-white" : "bg-zinc-100 text-zinc-600"}`} data-testid="tab-nuevo">
            Nuevo cierre
          </button>
          <button onClick={() => setTab("historial")} className={`px-4 py-2 rounded font-black uppercase tracking-widest text-xs ${tab === "historial" ? "bg-black text-white" : "bg-zinc-100 text-zinc-600"}`} data-testid="tab-historial">
            <History className="inline w-4 h-4 mr-1" />Historial ({historial.length})
          </button>
        </div>
      </header>

      {tab === "nuevo" ? (
        <>
          {/* Selector de período */}
          <Card className="p-4 flex items-end flex-wrap gap-3">
            <div className="w-full flex flex-wrap gap-2">
              <Button size="sm" variant={modoUlt ? "default" : "outline"} onClick={() => { setModoUlt(true); setSnapshot(null); }} data-testid="cm-modo-ultimo">Hacer cierre desde el último cierre</Button>
              <Button size="sm" variant={!modoUlt ? "default" : "outline"} onClick={() => { setModoUlt(false); setSnapshot(null); }} data-testid="cm-modo-manual">Rango manual</Button>
            </div>
            {autoOk ? (
              <div className="border rounded px-3 py-2 bg-emerald-50 border-emerald-300" data-testid="cm-inicio-auto">
                <Label className="text-xs uppercase tracking-widest">Inicia automáticamente</Label>
                <div className="text-sm font-bold">
                  {ult.ejecutado_at
                    ? `Cierre ${ult.mes_key} · ${new Date(ult.ejecutado_at).toLocaleString("es-GT", { timeZone: "America/Guatemala" })}`
                    : "Sin cierres confirmados: desde el inicio"}
                </div>
              </div>
            ) : (
              <div>
                <Label className="text-xs uppercase tracking-widest">Fecha inicial</Label>
                <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="w-48" data-testid="cm-desde" />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest">Fecha final</Label>
              <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="w-48" data-testid="cm-hasta" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Mes que se cierra</Label>
              <Input type="month" value={mes} onChange={(e) => setMesKey(e.target.value)} className="w-44" data-testid="cm-mes" />
            </div>
            <Button onClick={previsualizar} disabled={busy} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="cm-preview">
              <Eye className="w-4 h-4 mr-1" /> Vista previa
            </Button>
            <Button variant="outline" onClick={descargarPreviewPdf} disabled={busy} data-testid="cm-preview-pdf">
              <Printer className="w-4 h-4 mr-1" /> Imprimir Pre-cierre
            </Button>
            {snapshot && (
              <Button onClick={() => setConfirmOpen(true)} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 ml-auto" data-testid="cm-confirm">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Confirmar cierre
              </Button>
            )}
          </Card>

          <ReportePrevio rango={rango} />
          {snapshot && !confirmOpen && <Card className="p-3"><div className="text-xs font-black uppercase tracking-widest mb-2">Cuadre con el Dashboard</div><CuadreDashboard rango={rango} /></Card>}
          {snapshot && <SnapshotView snap={snapshot} isPreview onBancoDownload={descargarBancoPreview} onTodosDownload={(fmt, tipo) => descargarTodosPreview(fmt, tipo)} onPendientesDownload={(fmt) => descargarPendientesPreview(fmt)} onConciliacionDownload={(fmt) => descargarConciliacionPreview(fmt)} />}
        </>
      ) : (
        <Card className="p-4">
          <div className="text-lg font-semibold mb-3">Historial de cierres</div>
          {historial.length === 0 ? (
            <div className="text-center py-10 text-sm text-zinc-500">No hay cierres registrados aún.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-600 text-left">
                <tr>
                  <th className="px-3 py-2 border">N°</th>
                  <th className="px-3 py-2 border">Mes</th>
                  <th className="px-3 py-2 border">Desde → Hasta</th>
                  <th className="px-3 py-2 border">Ejecutado</th>
                  <th className="px-3 py-2 border">Por</th>
                  <th className="px-3 py-2 border text-right">Ventas</th>
                  <th className="px-3 py-2 border text-right">Facturado</th>
                  <th className="px-3 py-2 border text-right">Ganancia neta</th>
                  <th className="px-3 py-2 border">Estado</th>
                  <th className="px-3 py-2 border">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {historial.map((c) => (
                  <Fragment key={c.id}>
                  <tr className="hover:bg-zinc-50">
                    <td className="px-3 py-2 border font-black">#{c.numero_cierre}</td>
                    <td className="px-3 py-2 border">{c.mes_key}</td>
                    <td className="px-3 py-2 border whitespace-nowrap">{c.desde} → {c.hasta}</td>
                    <td className="px-3 py-2 border text-xs">{(c.ejecutado_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2 border text-xs">{c.ejecutado_por}</td>
                    <td className="px-3 py-2 border text-right tabular-nums">{formatMoney(c.totales?.ventas)}</td>
                    <td className="px-3 py-2 border text-right tabular-nums">{formatMoney(c.totales?.total_facturado)}</td>
                    <td className="px-3 py-2 border text-right tabular-nums font-bold">{formatMoney(c.totales?.ganancia_neta)}</td>
                    <td className="px-3 py-2 border">
                      <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${c.estado === "confirmado" ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"}`}>
                        {c.estado}
                      </span>
                    </td>
                    <td className="px-3 py-2 border">
                      <div className="flex gap-1 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => descargarCierrePdf(c.id)} data-testid={`hist-pdf-${c.numero_cierre}`}>
                          <FileText className="w-3 h-3 mr-1" />PDF
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => toggleCierreExpand(c)} data-testid={`hist-toggle-${c.numero_cierre}`}>
                          {expandedCierre[c.id] ? "Ocultar" : "Ver bancos"}
                        </Button>
                        {c.estado === "confirmado" && (
                          <Button size="sm" variant="outline" onClick={() => setReabrir({ open: true, id: c.id, motivo: "" })} className="text-orange-700 border-orange-300" data-testid={`hist-reabrir-${c.numero_cierre}`}>
                            <RotateCcw className="w-3 h-3 mr-1" />Reabrir
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedCierre[c.id] && (
                    <tr key={`${c.id}-exp`}>
                      <td colSpan={10} className="p-3 bg-zinc-50 border">
                        <div className="text-xs uppercase tracking-widest font-bold text-zinc-600 mb-2 flex items-center gap-2 flex-wrap">
                          <span>Reportes por banco — Cierre #{c.numero_cierre}</span>
                          <span className="flex gap-1 ml-auto flex-wrap">
                            <button type="button" onClick={() => descargarTodosHistorial(c.id, "xlsx")} className="px-3 py-1 text-[10px] uppercase tracking-widest font-black bg-blue-800 text-white rounded hover:bg-blue-900" data-testid={`hist-todos-xlsx-${c.numero_cierre}`}>TODOS Excel</button>
                            <button type="button" onClick={() => descargarTodosHistorial(c.id, "pdf")} className="px-3 py-1 text-[10px] uppercase tracking-widest font-black bg-blue-800 text-white rounded hover:bg-blue-900" data-testid={`hist-todos-pdf-${c.numero_cierre}`}>TODOS PDF</button>
                            <button type="button" onClick={() => descargarTodosHistorial(c.id, "xlsx", "ciudad")} className="px-3 py-1 text-[10px] uppercase tracking-widest font-black bg-pink-700 text-white rounded hover:bg-pink-800" data-testid={`hist-ciudad-xlsx-${c.numero_cierre}`}>Ciudad Excel</button>
                            <button type="button" onClick={() => descargarTodosHistorial(c.id, "pdf", "ciudad")} className="px-3 py-1 text-[10px] uppercase tracking-widest font-black bg-pink-700 text-white rounded hover:bg-pink-800" data-testid={`hist-ciudad-pdf-${c.numero_cierre}`}>Ciudad PDF</button>
                            <button type="button" onClick={() => descargarTodosHistorial(c.id, "xlsx", "departamento")} className="px-3 py-1 text-[10px] uppercase tracking-widest font-black bg-emerald-700 text-white rounded hover:bg-emerald-800" data-testid={`hist-depto-xlsx-${c.numero_cierre}`}>Depto Excel</button>
                            <button type="button" onClick={() => descargarTodosHistorial(c.id, "pdf", "departamento")} className="px-3 py-1 text-[10px] uppercase tracking-widest font-black bg-emerald-700 text-white rounded hover:bg-emerald-800" data-testid={`hist-depto-pdf-${c.numero_cierre}`}>Depto PDF</button>
                            <button type="button" onClick={() => descargarPendientesHistorial(c.id, "xlsx")} className="px-3 py-1 text-[10px] uppercase tracking-widest font-black bg-orange-700 text-white rounded hover:bg-orange-800" data-testid={`hist-pendientes-xlsx-${c.numero_cierre}`}>Pendientes Excel</button>
                            <button type="button" onClick={() => descargarPendientesHistorial(c.id, "pdf")} className="px-3 py-1 text-[10px] uppercase tracking-widest font-black bg-orange-700 text-white rounded hover:bg-orange-800" data-testid={`hist-pendientes-pdf-${c.numero_cierre}`}>Pendientes PDF</button>
                          </span>
                        </div>
                        <div className="grid gap-2 md:grid-cols-2">
                          {(expandedCierre[c.id].reportes_bancos || []).map((rb, i) => (
                            <div key={i} className="bg-white border rounded p-2 flex flex-wrap items-center gap-2 text-xs">
                              <div className="flex-1">
                                <div className="font-black">{rb.banco}</div>
                                <div className="text-zinc-500">{rb.movimientos_count} mov. · <span className="text-emerald-700 tabular-nums font-bold">{formatMoney(rb.total_recibido)}</span></div>
                              </div>
                              <Button size="sm" onClick={() => descargarBancoHistorial(c.id, rb.banco, "xlsx")} className="bg-emerald-600 hover:bg-emerald-700 h-7 text-[10px]" data-testid={`hist-banco-xlsx-${c.numero_cierre}-${i}`}>Excel</Button>
                              <Button size="sm" onClick={() => descargarBancoHistorial(c.id, rb.banco, "pdf")} className="bg-red-600 hover:bg-red-700 h-7 text-[10px]" data-testid={`hist-banco-pdf-${c.numero_cierre}-${i}`}>PDF</Button>
                            </div>
                          ))}
                          {(expandedCierre[c.id].reportes_bancos || []).length === 0 && (
                            <div className="text-zinc-400 text-xs">Sin bancos registrados en este cierre.</div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {/* Confirmar cierre */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Confirmar cierre
            </DialogTitle>
            <DialogDescription>
              De <b>{snapshot?.pedidos.length || 0}</b> pedidos con movimiento en el período, <b data-testid="cm-pasan-historial">{snapshot?.order_ids_a_cerrar?.length || 0}</b> (100% facturados o cancelados) pasan al historial financiero y{" "}
              <b data-testid="cm-quedan-activos">{(snapshot?.pedidos.length || 0) - (snapshot?.order_ids_a_cerrar?.length || 0)}</b> quedan activos (parciales y sin facturar). Se archivan <b>{snapshot?.gastos?.length || 0}</b> gastos.
              <br />
              Los pedidos no entregados siguen en Hoja de Pedidos. Se puede revertir con "Reabrir".
            </DialogDescription>
          </DialogHeader>
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm space-y-1">
            <div><b>Período:</b> {autoOk && ult?.ejecutado_at ? `desde el cierre #${ult.numero_cierre} (${new Date(ult.ejecutado_at).toLocaleString("es-GT", { timeZone: "America/Guatemala" })})` : desde} → {hasta}</div>
            <div><b>Ventas:</b> {formatMoney(snapshot?.totales?.ventas)}</div>
            <div><b>Total facturado:</b> {formatMoney(snapshot?.totales?.total_facturado)}</div>
            <div><b>Ganancia neta:</b> {formatMoney(snapshot?.totales?.ganancia_neta)}</div>
            <div className="text-[11px] text-amber-900">Calculado con las mismas reglas del Dashboard (ver tabla de cuadre).</div>
          </div>
          {confirmOpen && <CuadreDashboard rango={rango} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={busy}>Cancelar</Button>
            <Button onClick={confirmar} disabled={busy} className="bg-red-700 hover:bg-red-800" data-testid="cm-confirmar-btn">
              {busy ? "Cerrando..." : "Sí, ejecutar cierre"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reabrir */}
      <Dialog open={reabrir.open} onOpenChange={(v) => setReabrir((r) => ({ ...r, open: v }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reabrir cierre</DialogTitle>
            <DialogDescription>Los pedidos y gastos vinculados volverán a estar activos. Se registra motivo, usuario, fecha y hora.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-widest">Motivo</Label>
            <Textarea value={reabrir.motivo} onChange={(e) => setReabrir((r) => ({ ...r, motivo: e.target.value }))} rows={3} placeholder="Ej: corrección de factura duplicada #F-123" data-testid="reabrir-motivo" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReabrir({ open: false, id: null, motivo: "" })}>Cancelar</Button>
            <Button onClick={doReabrir} className="bg-orange-700 hover:bg-orange-800" data-testid="reabrir-confirm">Reabrir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SnapshotView({ snap, isPreview, onBancoDownload, onTodosDownload, onPendientesDownload, onConciliacionDownload }) {
  const [verPendientes, setVerPendientes] = useState(false);
  const t = snap.totales || {};
  return (
    <Card className={`p-4 border-2 ${isPreview ? "border-orange-300 bg-orange-50/40" : "border-emerald-300"}`}>
      {isPreview && (
        <div className="mb-3 flex items-center gap-2 text-orange-800 font-black uppercase tracking-widest text-xs">
          <AlertTriangle className="w-4 h-4" /> Pre-cierre / Borrador — no ejecutado
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 mb-4">
        <Kpi label="Pedidos" value={t.total_pedidos} />
        <Kpi label="100% facturados" value={t.completamente_facturados} tone="green" />
        <Kpi label="Parciales" value={t.parcialmente_facturados} tone="amber" />
        <Kpi label="Sin facturar" value={t.sin_facturar} tone="gray" />
        <Kpi label="Cancelados" value={t.cancelados} tone="red" />
        <Kpi label="Ventas" value={formatMoney(t.ventas)} tone="blue" />
        <Kpi label="Total facturado" value={formatMoney(t.total_facturado)} tone="blue" />
        <Kpi label="Pend. facturar" value={formatMoney(t.pendiente_facturar)} tone="amber" />
        <Kpi label="Total cobrado" value={formatMoney(t.total_cobrado)} tone="green" />
        <Kpi label="Pend. cobro" value={formatMoney(t.pendiente_cobro)} tone="amber" />
        <Kpi label="Anticipos" value={formatMoney(t.anticipos)} />
        <Kpi label="Gastos" value={formatMoney(t.gastos_total)} tone="red" />
        <Kpi label="Ganancia bruta" value={formatMoney(t.ganancia_bruta)} tone="blue" />
        <Kpi label="Ganancia neta" value={formatMoney(t.ganancia_neta)} tone="green" />
        <Kpi label="Costo (fact.)" value={formatMoney(t.costo_total)} />
      </div>

      {/* Bancos */}
      {snap.bancos?.length > 0 && (
        <div className="mb-4">
          <div className="text-xs uppercase tracking-widest font-bold text-zinc-600 mb-2">Ingresos por banco / método</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {snap.bancos.map((b, i) => (
              <div key={i} className="bg-white border rounded p-2 text-sm">
                <div className="font-bold">{b.banco}</div>
                <div className="text-xs text-zinc-500 capitalize">{b.metodo}</div>
                <div className="tabular-nums font-black text-emerald-700">{formatMoney(b.monto)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ronda 25 — Conciliación pedido por pedido (nuevas columnas + Excel/PDF) */}
      <div>
        <div className="text-xs uppercase tracking-widest font-bold text-zinc-600 mb-2 flex items-center gap-2 flex-wrap">
          <span>Conciliación pedido por pedido ({snap.pedidos.length})</span>
          {onConciliacionDownload && (
            <span className="flex gap-1 ml-auto">
              <button type="button" onClick={() => onConciliacionDownload("xlsx")} className="px-3 py-1 text-[10px] uppercase tracking-widest font-black bg-emerald-700 text-white rounded hover:bg-emerald-800" data-testid="cm-conc-xlsx">Excel</button>
              <button type="button" onClick={() => onConciliacionDownload("pdf")} className="px-3 py-1 text-[10px] uppercase tracking-widest font-black bg-red-700 text-white rounded hover:bg-red-800" data-testid="cm-conc-pdf">PDF</button>
            </span>
          )}
        </div>
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto border rounded">
          <table className="w-full text-xs">
            <thead className="bg-zinc-100 sticky top-0">
              <tr>
                <th className="px-2 py-1 text-left">N° Pedido</th>
                <th className="px-2 py-1 text-left">Fecha</th>
                <th className="px-2 py-1 text-left">Vendedor</th>
                <th className="px-2 py-1 text-left">Cliente</th>
                <th className="px-2 py-1 text-right">Total</th>
                <th className="px-2 py-1 text-center">Estado</th>
                <th className="px-2 py-1 text-right">Total Pagos</th>
                <th className="px-2 py-1 text-right">Total Facturado</th>
                <th className="px-2 py-1 text-right">Pend. Facturar</th>
              </tr>
            </thead>
            <tbody>
              {[...snap.pedidos].sort((a, b) => {
                const na = Number((String(a.numero || "").match(/(\d+)(?!.*\d)/) || [0, 0])[1]);
                const nb = Number((String(b.numero || "").match(/(\d+)(?!.*\d)/) || [0, 0])[1]);
                return na - nb;
              }).map((p) => (
                <tr key={p.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="px-2 py-1 font-bold font-mono">{p.numero}</td>
                  <td className="px-2 py-1">{p.fecha}</td>
                  <td className="px-2 py-1">{p.vendedor || "—"}</td>
                  <td className="px-2 py-1">{p.cliente}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{formatMoney(p.total)}</td>
                  <td className="px-2 py-1 text-center">
                    <span className={`inline-block text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${ESTADO_LABEL[p.estado]?.cls}`}>
                      {ESTADO_LABEL[p.estado]?.txt || p.estado}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-emerald-700">{formatMoney(p.total_cobrado)}</td>
                  <td className="px-2 py-1 text-right tabular-nums font-bold">{formatMoney(p.total_facturado)}</td>
                  <td className="px-2 py-1 text-right tabular-nums text-orange-700">{formatMoney(p.pendiente_facturar)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pedidos SIN facturar */}
      {(snap.pedidos_sin_facturar || []).length > 0 && (
        <div className="mt-4">
          <div className="text-xs uppercase tracking-widest font-bold text-orange-700 mb-2">
            Pedidos sin facturar ({snap.pedidos_sin_facturar.length})
          </div>
          <div className="overflow-x-auto max-h-[250px] overflow-y-auto border-2 border-orange-200 rounded">
            <table className="w-full text-xs">
              <thead className="bg-orange-100 sticky top-0"><tr>
                <th className="px-2 py-1 text-left">Pedido</th><th className="px-2 py-1 text-left">Fecha</th>
                <th className="px-2 py-1 text-left">Cliente</th><th className="px-2 py-1 text-right">Total</th>
                <th className="px-2 py-1 text-right">Pagado</th><th className="px-2 py-1 text-right">Saldo</th>
                <th className="px-2 py-1 text-right">Pend. fact.</th>
              </tr></thead>
              <tbody>
                {snap.pedidos_sin_facturar.map((p) => {
                  const saldo = Math.max(0, (p.total || 0) - (p.total_cobrado || 0));
                  return (
                    <tr key={p.id} className="border-t border-orange-100">
                      <td className="px-2 py-1 font-bold font-mono">{p.numero}</td>
                      <td className="px-2 py-1">{p.fecha}</td>
                      <td className="px-2 py-1">{p.cliente}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{formatMoney(p.total)}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-emerald-700">{formatMoney(p.total_cobrado)}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{formatMoney(saldo)}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{formatMoney(p.pendiente_facturar)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reportes individuales por banco */}
      {(snap.reportes_bancos || []).length > 0 && (
        <div className="mt-4 space-y-3">
          <div className="text-xs uppercase tracking-widest font-bold text-zinc-600 flex items-center gap-2 flex-wrap">
            <span>Reportes individuales por banco/cuenta ({snap.reportes_bancos.length})</span>
            {onTodosDownload && (
              <span className="flex gap-1 ml-auto flex-wrap">
                <button type="button" onClick={() => onTodosDownload("xlsx")} className="px-3 py-1 text-[10px] uppercase tracking-widest font-black bg-blue-800 text-white rounded hover:bg-blue-900" data-testid="cm-todos-xlsx">TODOS Excel</button>
                <button type="button" onClick={() => onTodosDownload("pdf")} className="px-3 py-1 text-[10px] uppercase tracking-widest font-black bg-blue-800 text-white rounded hover:bg-blue-900" data-testid="cm-todos-pdf">TODOS PDF</button>
                <button type="button" onClick={() => onTodosDownload("xlsx", "ciudad")} className="px-3 py-1 text-[10px] uppercase tracking-widest font-black bg-pink-700 text-white rounded hover:bg-pink-800" data-testid="cm-ciudad-xlsx">Ciudad (A) Excel</button>
                <button type="button" onClick={() => onTodosDownload("pdf", "ciudad")} className="px-3 py-1 text-[10px] uppercase tracking-widest font-black bg-pink-700 text-white rounded hover:bg-pink-800" data-testid="cm-ciudad-pdf">Ciudad (A) PDF</button>
                <button type="button" onClick={() => onTodosDownload("xlsx", "departamento")} className="px-3 py-1 text-[10px] uppercase tracking-widest font-black bg-emerald-700 text-white rounded hover:bg-emerald-800" data-testid="cm-depto-xlsx">Depto (B) Excel</button>
                <button type="button" onClick={() => onTodosDownload("pdf", "departamento")} className="px-3 py-1 text-[10px] uppercase tracking-widest font-black bg-emerald-700 text-white rounded hover:bg-emerald-800" data-testid="cm-depto-pdf">Depto (B) PDF</button>
              </span>
            )}
          </div>
          {snap.reportes_bancos.map((rb, i) => (
            <details key={i} className="border rounded p-2" data-testid={`cm-banco-${i}`}>
              <summary className="cursor-pointer font-bold text-sm flex flex-wrap items-center gap-2">
                <span>{rb.banco}</span>
                <span className="text-emerald-700 tabular-nums">{formatMoney(rb.total_recibido)}</span>
                <span className="text-zinc-500">· {rb.movimientos_count} movimientos</span>
                {onBancoDownload && (
                  <span className="ml-auto flex gap-1">
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); onBancoDownload(rb.banco, "xlsx"); }}
                      className="px-2 py-1 text-[10px] uppercase tracking-widest font-black bg-emerald-600 text-white rounded hover:bg-emerald-700"
                      data-testid={`cm-banco-xlsx-${i}`}
                    >Excel</button>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); onBancoDownload(rb.banco, "pdf"); }}
                      className="px-2 py-1 text-[10px] uppercase tracking-widest font-black bg-red-600 text-white rounded hover:bg-red-700"
                      data-testid={`cm-banco-pdf-${i}`}
                    >PDF</button>
                  </span>
                )}
              </summary>
              <table className="w-full text-[11px] mt-2">
                <thead className="bg-teal-50">
                  <tr>
                    <th className="px-1 py-1 text-left">Pedido</th>
                    <th className="px-1 py-1 text-left">F.Pago</th>
                    <th className="px-1 py-1 text-left">Cliente</th>
                    <th className="px-1 py-1 text-right">Recibido</th>
                    <th className="px-1 py-1 text-right">T.Pedido</th>
                    <th className="px-1 py-1 text-right">Facturado</th>
                    <th className="px-1 py-1 text-right">Saldo</th>
                    <th className="px-1 py-1 text-left">#Fact</th>
                    <th className="px-1 py-1 text-left">Ref</th>
                  </tr>
                </thead>
                <tbody>
                  {rb.movimientos.map((m, j) => (
                    <tr key={j} className="border-t">
                      <td className="px-1 py-0.5 font-mono">{m.numero_pedido}</td>
                      <td className="px-1 py-0.5">{m.fecha_pago || "—"}</td>
                      <td className="px-1 py-0.5">{m.cliente}</td>
                      <td className="px-1 py-0.5 text-right tabular-nums font-bold text-emerald-700">{formatMoney(m.monto_recibido)}</td>
                      <td className="px-1 py-0.5 text-right tabular-nums">{formatMoney(m.total_pedido)}</td>
                      <td className="px-1 py-0.5 text-right tabular-nums">{formatMoney(m.monto_facturado)}</td>
                      <td className="px-1 py-0.5 text-right tabular-nums">{formatMoney(m.saldo_pendiente)}</td>
                      <td className="px-1 py-0.5">{m.numero_factura || "—"}</td>
                      <td className="px-1 py-0.5">{m.referencia || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          ))}
        </div>
      )}

      {/* Ronda 23 — Revisar pendientes de facturar + imprimir */}
      {onPendientesDownload && (
        <div className="mt-4 border-2 border-orange-300 rounded p-3 bg-orange-50">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setVerPendientes((v) => !v)}
              className="px-4 py-1.5 text-[11px] uppercase tracking-widest font-black bg-orange-600 text-white rounded hover:bg-orange-700"
              data-testid="cm-revisar-pendientes"
            >
              {verPendientes ? "Ocultar pendientes" : `Revisar pendientes de facturar (${(snap.pedidos || []).filter((p) => Number(p.pendiente_facturar || 0) > 0.005).length})`}
            </button>
            {verPendientes && (
              <span className="flex gap-1 ml-auto">
                <button type="button" onClick={() => onPendientesDownload("xlsx")} className="px-3 py-1 text-[10px] uppercase tracking-widest font-black bg-orange-700 text-white rounded hover:bg-orange-800" data-testid="cm-pendientes-xlsx">Imprimir Excel</button>
                <button type="button" onClick={() => onPendientesDownload("pdf")} className="px-3 py-1 text-[10px] uppercase tracking-widest font-black bg-orange-700 text-white rounded hover:bg-orange-800" data-testid="cm-pendientes-pdf">Imprimir PDF</button>
              </span>
            )}
          </div>
          {verPendientes && (
            <div className="mt-3 overflow-x-auto max-h-[320px] overflow-y-auto border border-orange-200 rounded bg-white">
              <table className="w-full text-xs">
                <thead className="bg-orange-100 sticky top-0">
                  <tr>
                    <th className="px-2 py-1 text-left">N° Pedido</th>
                    <th className="px-2 py-1 text-left">Fecha</th>
                    <th className="px-2 py-1 text-left">Cliente</th>
                    <th className="px-2 py-1 text-left">Dpto/Ciudad</th>
                    <th className="px-2 py-1 text-right">Total</th>
                    <th className="px-2 py-1 text-right">Facturado</th>
                    <th className="px-2 py-1 text-right">Pend. facturar</th>
                  </tr>
                </thead>
                <tbody>
                  {(snap.pedidos || []).filter((p) => Number(p.pendiente_facturar || 0) > 0.005)
                    .sort((a, b) => (a.numero || "").localeCompare(b.numero || "", undefined, { numeric: true }))
                    .map((p) => (
                      <tr key={p.id} className="border-t border-orange-100" data-testid={`cm-pend-${p.numero}`}>
                        <td className="px-2 py-1 font-mono font-bold">{p.numero}</td>
                        <td className="px-2 py-1">{p.fecha}</td>
                        <td className="px-2 py-1">{p.cliente}</td>
                        <td className="px-2 py-1 capitalize">{(p.envio_tipo || "").toLowerCase() === "ciudad" ? "Ciudad" : (p.departamento || p.envio_nombre || "Departamento")}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{formatMoney(p.total)}</td>
                        <td className="px-2 py-1 text-right tabular-nums text-emerald-700">{formatMoney(p.total_facturado)}</td>
                        <td className="px-2 py-1 text-right tabular-nums font-black text-orange-800">{formatMoney(p.pendiente_facturar)}</td>
                      </tr>
                    ))}
                  {(snap.pedidos || []).filter((p) => Number(p.pendiente_facturar || 0) > 0.005).length === 0 && (
                    <tr><td colSpan={7} className="px-2 py-4 text-center text-zinc-500">Sin pedidos pendientes de facturar en este período 🎉</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Conciliación pedido por pedido (LEGACY - eliminado en favor del nuevo split arriba) */}
      <div style={{display: 'none'}}>
        <div className="text-xs uppercase tracking-widest font-bold text-zinc-600 mb-2">Conciliación (legacy view)</div>
      </div>

      {/* Conciliación general */}
      <div className="mt-4 p-3 bg-zinc-100 rounded text-xs">
        <b>Mismas reglas del Dashboard:</b> Ventas = pedidos creados en el período <span className="font-black tabular-nums">{formatMoney(t.ventas)}</span> · Facturado sin cancelados <span className="font-black tabular-nums">{formatMoney(t.total_facturado)}</span> · Por facturar total (se traslada) <span className="font-black tabular-nums">{formatMoney(t.pendiente_facturar)}</span>
      </div>
    </Card>
  );
}

function Kpi({ label, value, tone }) {
  const toneMap = {
    green: "bg-emerald-50 text-emerald-800",
    amber: "bg-amber-50 text-amber-800",
    red: "bg-red-50 text-red-800",
    blue: "bg-blue-50 text-blue-900",
    gray: "bg-zinc-100 text-zinc-700",
  };
  return (
    <div className={`p-2 rounded ${toneMap[tone] || "bg-white border"}`}>
      <div className="text-[9px] uppercase tracking-widest font-black opacity-70 truncate">{label}</div>
      <div className="text-base font-black tabular-nums">{value}</div>
    </div>
  );
}
